// =============================================================================
// ui/media/VoiceNotes.tsx — record, play back and detach voice notes.
// -----------------------------------------------------------------------------
// Ported from `attachRecordingControls` (index.html:23090). Beta renders one
// `<audio controls>` per note with the filename beside it and a × to remove, and
// a Record / Stop button underneath with a live m:ss counter.
//
// ── 🚨 THE COUNTER IS NOT DECORATION ──
//
// It is the only feedback that the mic is live. Beta added the 500-byte floor in
// `recorder.ts` because recordings were coming back empty; the counter is the
// other half of that fix — a trader who can see "Recording 0:04" knows the thing
// is running before they talk for a minute into a muted input.
//
// ── 🚨 TWO CALLERS, TWO COMMIT MODELS, ONE COMPONENT ──
//
// The journal writes each change straight to `journal_entries.recordings` — its
// own column, its own writer, no form draft over it, so there is nothing to race
// and asking the user to press Save afterwards would only lose notes.
//
// The trade form is the opposite: `recordings` is a column of the trade row that
// the form's own Save owns, and a second writer would fight it. There the list
// is part of the draft.
//
// So `onChange` may be either. It returns `void` for a draft edit, or a promise
// of an error string (null on success) for a caller that persists. The component
// does not know or care which — but it does not invent a "Saved" claim either,
// which is why there is no success message here.
//
// ── REMOVE CONFIRMS WHEN THE REMOVAL IS IMMEDIATE ──
//
// A chart capture can be retaken from the platform; a note spoken during a live
// session cannot be re-recorded at all. Where the removal writes at once
// (`confirmRemove`), it asks. In the trade form it does not, because not saving
// the form is already the undo.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { mediaFilename, recordingExt, uploadMedia } from '../../io/storage/media';
import { startRecording } from '../../io/storage/recorder';
import type { RecorderHandle } from '../../io/storage/recorder';
import { useMediaObjectUrl } from './useMediaObjectUrl';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface VoiceNotesProps {
  files: readonly string[];
  /** Filename stem — `${date}-journal` on the journal, `${date}-trade-N` on a trade. */
  prefix: string;
  /**
   * Commit the new list.
   *
   * Return nothing for a draft edit, or a promise of an error message (null when
   * it saved) for a caller that writes immediately. See the header.
   */
  onChange: (next: string[]) => void | Promise<string | null>;
  /** Ask before dropping a note. On for callers that write at once. */
  confirmRemove?: boolean;
  /** Names the thing being detached in the confirm — "2026-07-30", "this trade". */
  ownerLabel?: string;
}

export function VoiceNotes(
  { files, prefix, onChange, confirmRemove = false, ownerLabel = 'this entry' }: VoiceNotesProps,
) {
  const { sb } = useSupabase();

  const [handle, setHandle] = useState<RecorderHandle | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read inside the recorder's `onStop` callback, which is created once and
  // fires much later — the closure's copy of `files` would be whatever the list
  // was when Record was pressed.
  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => {
    if (!handle) return;
    setElapsed(0);
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 500);
    return () => clearInterval(t);
  }, [handle]);

  const commit = useCallback(async (next: string[]) => {
    // ⚠ A STRING BACK IS A FAILURE, and it must not be swallowed. `void` is the
    // draft caller, which cannot fail because nothing left the browser.
    const r = await onChange(next);
    if (typeof r === 'string') setError(r);
  }, [onChange]);

  const start = useCallback(async () => {
    setError(null);
    const h = await startRecording({
      onError: (m) => { setError(m); setHandle(null); },
      onStop: (blob) => {
        setHandle(null);
        setBusy(true);
        void (async () => {
          try {
            const current = filesRef.current;
            const name = mediaFilename(prefix, current.length + 1, recordingExt(blob.type));
            // ⚠ Uploaded FIRST. A row listing a filename whose object never
            // landed is a player that will not play, with no error anywhere.
            await uploadMedia(sb, 'recordings', name, blob);
            await commit([...current, name]);
          } catch (e) {
            setError(reportFailure('upload voice note', e, 'The upload failed.'));
          } finally {
            setBusy(false);
          }
        })();
      },
    });
    if (h) setHandle(h);
  }, [sb, prefix, commit]);

  const remove = async (i: number) => {
    const fn = files[i];
    // ⚠ CONFIRMS ONLY WHERE THE REMOVAL IS IMMEDIATE. See the header.
    if (confirmRemove
      && !window.confirm(`Detach ${fn} from ${ownerLabel}? The file is kept in storage.`)) return;
    setError(null);
    setBusy(true);
    await commit(files.filter((_, n) => n !== i));
    setBusy(false);
  };

  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <div className="vn-wrap">
      {files.length ? (
        <div className="vn-list">
          {files.map((fn, i) => (
            <Note key={fn} filename={fn} onRemove={() => void remove(i)} disabled={busy} />
          ))}
        </div>
      ) : (
        <p className="muted">No voice notes.</p>
      )}

      <div className="row gap-8 vn-controls">
        {handle ? (
          <button type="button" className="btn" onClick={() => handle.stop()}>
            <span className="vn-rec-dot" /> Stop
          </button>
        ) : (
          <button type="button" className="btn" disabled={busy} onClick={() => void start()}>
            {busy ? 'Saving…' : 'Record'}
          </button>
        )}
        {handle ? <span className="muted vn-timer">Recording {mmss}</span> : null}
      </div>

      {error ? (
        <div role="alert" className="import-alert vn-error"><b>Not saved.</b> {error}</div>
      ) : null}
    </div>
  );
}

function Note(
  { filename, onRemove, disabled }:
  { filename: string; onRemove: () => void; disabled: boolean },
) {
  const { url, error, loading } = useMediaObjectUrl('recordings', filename);
  return (
    <div className="row gap-8 vn-row">
      {url
        ? <audio controls src={url} />
        : <span className="muted">{loading ? 'Loading…' : (error ?? 'Unavailable')}</span>}
      <span className="muted vn-name">{filename}</span>
      <button
        type="button" className="btn danger btn-sm"
        disabled={disabled} aria-label={`Remove ${filename}`} title={`Remove ${filename}`}
        onClick={onRemove}
      >×</button>
    </div>
  );
}
